class LazyScriptLoader {
  constructor() {
    this.scripts = [];
    this.selector = "script[data-loading=\"lazy\"][data-src]";
    this.loadAfterMsIfNoInteraction = 45000;
    this.loadTimer = null;
    this.observer = null;
    this.boundLoaders = new WeakSet();
  }

  init() {
    this.scripts = Array.from(document.querySelectorAll(this.selector));

    if (this.scripts.length === 0) return;

    if ("IntersectionObserver" in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const mode = entry.target.dataset.loadOn || "viewport";
            if (mode === "viewport") {
              this.loadScript(entry.target);
              this.observer.unobserve(entry.target);
            }
          }
        });
      }, { rootMargin: "400px 0px" });

      this.scripts.forEach((script) => this.observer.observe(script));
    }

    this.scripts.forEach((script) => this.bindScript(script));
    this.loadTimer = setTimeout(() => this.loadScriptsByMode(["idle", "viewport"]), this.loadAfterMsIfNoInteraction);
  }

  loadScripts() {
    this.scripts.forEach((script) => {
      if (script.dataset.loading === "loaded") return;
      this.loadScript(script);
    });
  }

  loadScriptsByMode(modes) {
    this.scripts.forEach((script) => {
      if (script.dataset.loading === "loaded") return;
      const mode = script.dataset.loadOn || "viewport";
      if (modes.includes(mode)) this.loadScript(script);
    });
  }

  bindScript(script) {
    if (this.boundLoaders.has(script)) return;
    this.boundLoaders.add(script);

    const mode = script.dataset.loadOn || "viewport";
    if (mode === "search") {
      const load = () => this.loadScript(script);
      const selector = 'a[href="#drawer-search"], .search-button, input[type="search"], input[name="q"], form[action*="/search"]';
      document.addEventListener("focusin", (event) => {
        if (event.target && event.target.closest(selector)) load();
      }, { passive: true });
      document.addEventListener("pointerdown", (event) => {
        if (event.target && event.target.closest(selector)) load();
      }, { passive: true });
      return;
    }

    if (mode === "quick-buy") {
      const load = () => this.loadScript(script);
      document.addEventListener("pointerdown", (event) => {
        if (event.target && event.target.closest("quick-buy, .quick-buy, .quick-buy_link")) load();
      }, { passive: true });
      document.addEventListener("focusin", (event) => {
        if (event.target && event.target.closest("quick-buy, .quick-buy, .quick-buy_link")) load();
      }, { passive: true });
      return;
    }

    if (mode === "facets") {
      const load = () => this.loadScript(script);
      const selector = '.filters-button, #drawer-filter, drawer-filter, facet-filters-form, sort-by, #sort-by, [data-filtered-count]';
      document.addEventListener("pointerdown", (event) => {
        if (event.target && event.target.closest(selector)) load();
      }, { passive: true });
      document.addEventListener("pointerover", (event) => {
        if (event.target && event.target.closest(selector)) load();
      }, { passive: true });
      document.addEventListener("focusin", (event) => {
        if (event.target && event.target.closest(selector)) load();
      }, { passive: true });
      document.addEventListener("change", (event) => {
        if (event.target && event.target.closest(selector)) load();
      }, { passive: true, capture: true });
      return;
    }

    if (mode === "swiper") {
      const load = () => this.loadScript(script);
      const selector = 'cards-slider, mega-menu-slider, .swiper, .header-item__link, [href="#drawer-menu"], [data-drawer-open], [data-menu]';
      if ("IntersectionObserver" in window) {
        const swiperObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            load();
            swiperObserver.disconnect();
          }
        }, { rootMargin: "800px 0px" });
        document.querySelectorAll("cards-slider, mega-menu-slider, .swiper").forEach((node) => swiperObserver.observe(node));
      }
      document.addEventListener("pointerdown", (event) => {
        if (event.target && event.target.closest(selector)) load();
      }, { passive: true });
      document.addEventListener("focusin", (event) => {
        if (event.target && event.target.closest(selector)) load();
      }, { passive: true });
      return;
    }

    if (mode === "idle") return;

    ["pointerdown", "focusin", "keydown", "touchstart"].forEach((eventName) => {
      document.addEventListener(eventName, () => this.loadScript(script), { passive: true, once: true });
    });
  }

  loadScript(script) {
    if (!script || script.dataset.loading === "loaded") return;
    const source = script.dataset.src;
    if (!source) return;
    script.dataset.loading = "loaded";
    script.addEventListener("load", () => {
      document.dispatchEvent(new CustomEvent("mma:lazy-script-loaded", { detail: { src: script.src } }));
      if (script.src.includes("swiper-bundle")) {
        document.dispatchEvent(new CustomEvent("mma:swiper-ready"));
      }
    }, { once: true });
    script.src = source;
    script.removeAttribute("data-src");
  }
}

class LazyVideoLoader {
  constructor() {
    this.selector = "video.js-lazy-video";
    this.videos = [];
    this.observer = null;
  }

  init() {
    this.videos = Array.from(document.querySelectorAll(this.selector));

    if (this.videos.length === 0) return;

    this.videos.forEach((video) => {
      video.preload = "none";
      video.addEventListener("pointerdown", () => this.loadVideo(video, true), { passive: true, once: true });
      video.addEventListener("play", () => this.loadVideo(video, true), { once: true });
    });

    if (!("IntersectionObserver" in window)) {
      this.videos.forEach((video) => this.loadVideo(video, video.dataset.autoplay === "true"));
      return;
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;

        if (entry.isIntersecting) {
          this.loadVideo(video, video.dataset.autoplay === "true");
        } else if (video.dataset.autoplay === "true" && video.dataset.loading === "loaded") {
          video.pause();
        }
      });
    }, { rootMargin: "600px 0px" });

    this.videos.forEach((video) => this.observer.observe(video));
  }

  loadVideo(video, shouldPlay) {
    if (video.dataset.loading !== "loaded") {
      Array.from(video.querySelectorAll("source[data-src]")).forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute("data-src");
      });
      video.dataset.loading = "loaded";
      video.load();
    }

    if (!shouldPlay) return;

    video.muted = true;
    video.playsInline = true;
    const playAttempt = video.play();
    if (playAttempt && typeof playAttempt.catch === "function") {
      playAttempt.catch(() => {});
    }
  }
}

const loader = new LazyScriptLoader();
const videoLoader = new LazyVideoLoader();
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    loader.init();
    videoLoader.init();
  }, { once: true });
} else {
  loader.init();
  videoLoader.init();
}
